import { useState, useEffect, useContext } from "react";
import { trans } from "@/service/i18n/i18n";
// eslint-disable-next-line import/named
import { useForm } from "react-hook-form";
import { useImmer } from "use-immer";
import { preSubmit } from "@/forms/validation";
import { createFormFields } from "./model";
import { handleUserError } from "@/service/client-api/error-handler";
import { rcn } from "@/service/class-naming";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { clientGet, clientPatch } from "@/service/client-api/client-api";
import { ResourceContext } from "@/contexts/resource-context";
import { logPermissionEvent } from "@/components/common/permission/service";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { canHaveSpecialization, isDoctorType } from "@/service/user-groups";
import { SelectField } from "@/forms/fields/select-field/select-field";
import { CheckboxField } from "@/forms/fields/checkbox-field/checkbox-field";
import { Form } from "@/components/ui/form";
import { LoaderIcon } from "lucide-react";
import { FormError } from "@/forms/form-error";
import { FieldsContainer } from "@/forms/fields-container";

export const SetStatus = ({
  isNewsletterRequired,
  access,
  setAccess,
}: {
  isNewsletterRequired: boolean;
  access: any;
  setAccess: (access: string) => void;
}) => {
  const [fields, setFields] = useImmer<ReturnType<typeof createFormFields>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { logView } = useContext(ResourceContext);

  const user = useContext(UserContext);

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { handleSubmit } = form;

  useEffect(() => {
    const fetchData = async () => {
      const { type, dataForType } = await clientGet("profile.info");
      const { doctor, dentist } = await clientGet("profile.data_info");

      if (isDoctorType(type)) {
        setFields(
          createFormFields(
            doctor.specialization,
            doctor.status,
            dataForType,
            user.defaultNewsletter,
            isNewsletterRequired,
          ),
        );
      } else {
        setFields(
          createFormFields(
            dentist.specialization,
            dentist.status,
            dataForType,
            user.defaultNewsletter,
            isNewsletterRequired,
          ),
        );
      }
    };

    fetchData();
  }, []);

  if (Object.keys(fields).length === 0) {
    return <div className={rcn("permission__content", { load: true })}></div>;
  }

  const onSubmit = async (values: Record<keyof typeof fields, any>) => {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    const oldSnapshot = (await clientGet("profile.info")) as any;

    const payload = {} as {
      newsletter: boolean;
      dataForType: Omit<Record<keyof typeof fields, any>, "newsletter">;
    };
    if (values.newsletter) {
      payload.newsletter = values.newsletter;
    }

    // SelectField trzyma w formularzu samą wartość (string), nie opcję
    payload.dataForType = {
      status: values.status,
      ...(canHaveSpecialization(values.status) && {
        specializationsByUser: values.specializationsByUser,
      }),
    };

    try {
      await clientPatch("profile.update", {}, payload);

      await logPermissionEvent("doctor_choose_status", logView, oldSnapshot);

      setAccess("review_status");
    } catch (caughtError) {
      setLoading(false);
      handleUserError(caughtError, setError, setFields);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h2
          className={`text-card-foreground mb-6 text-center text-2xl font-semibold`}
        >
          {trans("hello")} {user.shortName},
        </h2>
        <p className={`text-foreground mb-6 text-center text-base font-normal`}>
          {access.studentUpdate
            ? trans("permission.actions.doctor.set_etap_and_specialization")
            : access.alreadyHasAccess
            ? trans("permission.actions.doctor.try_access_as")
            : trans("permission.actions.doctor.set_etap")}
        </p>

        <FieldsContainer>
          <SelectField field={fields.status} />
          {canHaveSpecialization(
            form.watch("status") ?? fields.status?.value,
          ) && <SelectField field={fields.specializationsByUser} />}

          {!user.defaultNewsletter.isSignedUp && fields.newsletter && (
            <CheckboxField field={fields.newsletter} />
          )}
        </FieldsContainer>

        <Button type="submit" className={"mb-4 w-full"}>
          {loading && <LoaderIcon className={"animate-spin"} size={16} />}{" "}
          {trans("next")}
        </Button>

        <FormError error={error} />
      </form>
    </Form>
  );
};
